/*
 * Phase 4A Commit 4Ad-1 — wholesale-dashboard.js
 *
 * Client-side buyer dashboard. Fetches the read-only payload from
 * /apps/dashboard/wholesale/me and renders profile + KPIs + orders
 * + invoices.
 *
 * Auth: Shopify Customer Account session via the App Proxy. The
 * buyer signs in at account.livecircadia.ca (Shopify-hosted), then
 * lands on /pages/wholesale-dashboard. No tokens, no sessionStorage,
 * no Authorization headers — the App Proxy carries
 * logged_in_customer_id on the signed query params and the backend
 * middleware (requireAppProxySignature) trusts it.
 *
 * Distinct from admin-wholesale-queue.js (4Ab), which uses a
 * sessionStorage-stashed admin JWT — admins aren't Shopify
 * customers, so they get a separate password+TOTP flow. Wholesale
 * buyers ARE Shopify customers (B2B Company contacts), so they
 * inherit the DTC dashboard's auth pattern verbatim.
 *
 * Not-a-wholesale-account state: if the backend returns 404 with
 * code 'not_a_wholesale_account' (DTC customer hitting this page,
 * or pre-approval contact whose contacts row hasn't been INSERTed),
 * hide the live sections and show the apply-CTA empty state.
 */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }

  var ME_URL = '/apps/dashboard/wholesale/me';

  var displayNameEl     = $('[data-wholesale-display-name]');
  var accountNumberEl   = $('[data-wholesale-account-number]');
  var badgesEl          = $('[data-wholesale-badges]');
  var kpisEl            = $('[data-wholesale-kpis]');
  var ordersListEl      = $('[data-wholesale-orders-list]');
  var invoicesListEl    = $('[data-wholesale-invoices-list]');
  var emptyEl           = $('[data-wholesale-empty]');
  var ordersSectionEl   = $('[data-wholesale-orders-section]');
  var invoicesSectionEl = $('[data-wholesale-invoices-section]');

  // ─── Escape helper (XSS-safe text insertion) ────────────────────

  function escapeHtml(s) {
    if (s === null || s === undefined) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function fmtCad(decimalString) {
    if (decimalString === null || decimalString === undefined) return '—';
    return '$' + decimalString;
  }

  function fmtDate(iso) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString();
    } catch (_) {
      return iso;
    }
  }

  // ─── Render: not-a-wholesale-account state ──────────────────────

  // Finding 7 copy. Two states, written to read as one voice.
  //
  // NEITHER CONFIRMS WHY. The inactive copy does not say revoked, suspended,
  // overdue, non-payment, or rejected, and implies no fault: a buyer whose access
  // lapsed for a billing reason and one whose application is still being reviewed
  // both see the same true sentence. The reason is a conversation for a human,
  // which is why the only action offered is a way to reach one.
  var INACTIVE_HEADING = 'Your wholesale access isn’t active right now.';
  var INACTIVE_BODY =
    'Your account is on file, so nothing has been lost. Wholesale ordering and ' +
    'pricing are just paused for now. Email <a href="mailto:wholesale@livecircadia.ca" ' +
    'style="color:var(--drift);">wholesale@livecircadia.ca</a> and we’ll sort it out with you.';

  function renderNotAWholesaleAccount(code) {
    if (ordersSectionEl) ordersSectionEl.hidden = true;
    if (invoicesSectionEl) invoicesSectionEl.hidden = true;
    if (kpisEl) kpisEl.hidden = true;
    if (badgesEl) badgesEl.hidden = true;
    if (displayNameEl) displayNameEl.textContent = '';
    if (accountNumberEl) accountNumberEl.textContent = '';
    if (emptyEl) {
      if (code === 'wholesale_access_inactive') {
        // Rewrite the liquid-rendered never-applied copy in place. The heading and
        // paragraph are the only two children, so this swaps both rather than
        // leaving "Not a wholesale account." above the new body.
        var heading = emptyEl.querySelector('h2');
        var para = emptyEl.querySelector('p');
        if (heading) heading.textContent = INACTIVE_HEADING;
        if (para) para.innerHTML = INACTIVE_BODY;
      }
      emptyEl.hidden = false;
    }
  }

  // ─── Render: header + badges ────────────────────────────────────

  function renderHeader(body) {
    var profile = body.profile;
    if (displayNameEl) displayNameEl.textContent = profile.display_name + '.';
    if (accountNumberEl) accountNumberEl.textContent = profile.account_number + ' · ' + profile.legal_business_name;

    var badges = [];
    if (body.sampler_available) {
      badges.push('<span class="wholesale-pill" style="background:var(--rise);color:#000;">Sampler available: claim free cases</span>');
    }
    if (body.first_order) {
      // Freight messaging dropped from the pill — locked model is
      // freight invoiced separately on every order (first paid order
      // is freight-free within Ontario, but the pill isn't the right
      // surface for the nuance; the order-page caption + pricing-
      // sheet PDF carry the full sentence).
      badges.push('<span class="wholesale-pill" style="background:var(--drift);color:#fff;">First order: pay-on-order</span>');
    }
    if (profile.has_overdue_invoices) {
      badges.push('<span class="wholesale-pill" style="background:#c0392b;color:#fff;">Overdue invoice(s): please review</span>');
    }
    if (badgesEl) badgesEl.innerHTML = badges.join(' ');
  }

  // ─── Render: KPI strip ──────────────────────────────────────────

  function renderKpis(body) {
    if (!kpisEl) return;
    var openInvoices = body.invoices.filter(function (i) { return i.status === 'open' || i.status === 'overdue'; }).length;
    var totalSpent = body.orders.reduce(function (s, o) {
      var n = Number.parseFloat(o.total_cad);
      return s + (Number.isFinite(n) ? n : 0);
    }, 0);
    var lastShipment = body.orders.find(function (o) { return o.shipped_at !== null; });
    var samplerLabel = body.sampler_available ? 'Available' : (body.profile.is_tax_exempt ? 'N/A' : 'Claimed');
    var html = ''
      + kpiBox('Total ordered', '$' + totalSpent.toFixed(2))
      + kpiBox('Last shipment', lastShipment ? fmtDate(lastShipment.shipped_at) : '—')
      + kpiBox('Open invoices', String(openInvoices))
      + kpiBox('Sampler', samplerLabel);
    kpisEl.innerHTML = html;
  }
  function kpiBox(label, value) {
    return '<div class="wholesale-kpi">' +
      '<div class="caption" style="color:var(--fg-mute);">' + escapeHtml(label) + '</div>' +
      '<div style="font-size:24px;margin-top:8px;">' + escapeHtml(value) + '</div>' +
    '</div>';
  }

  // ─── Render: orders table ───────────────────────────────────────

  function renderOrders(orders) {
    if (!ordersListEl) return;
    if (orders.length === 0) {
      ordersListEl.innerHTML = '<div class="caption" style="color:var(--fg-mute);padding:16px 0;">No orders yet.</div>';
      return;
    }
    var rows = orders.map(function (o) {
      var statusClass = 'status status-' + o.status;
      var trackingCell = o.tracking_number
        ? '<span style="font-family:var(--mono);font-size:11px;">' + escapeHtml(o.tracking_carrier || '') + ' ' + escapeHtml(o.tracking_number) + '</span>'
        : '—';
      var refundedNote = (Number.parseFloat(o.refunded_amount_cad) > 0)
        ? ' · refunded ' + fmtCad(o.refunded_amount_cad)
        : '';
      return '<tr>' +
        '<td><strong>' + escapeHtml(o.po_number) + '</strong></td>' +
        '<td>' + fmtDate(o.created_at) + '</td>' +
        '<td>' + o.total_case_count + ' case' + (o.total_case_count === 1 ? '' : 's') +
          ' <span class="caption" style="color:var(--fg-mute);">(' + escapeHtml(o.volume_tier) + ')</span></td>' +
        '<td>' + fmtCad(o.total_cad) + escapeHtml(refundedNote) + '</td>' +
        '<td><span class="' + statusClass + '">' + escapeHtml(o.status) + '</span></td>' +
        '<td>' + trackingCell + '</td>' +
      '</tr>';
    }).join('');
    ordersListEl.innerHTML =
      '<table class="wholesale-table">' +
        '<thead><tr>' +
          '<th>PO</th><th>Date</th><th>Cases</th><th>Total</th><th>Status</th><th>Tracking</th>' +
        '</tr></thead>' +
        '<tbody>' + rows + '</tbody>' +
      '</table>';
  }

  // ─── Render: invoices table ─────────────────────────────────────

  function renderInvoices(invoices) {
    if (!invoicesListEl) return;
    if (invoices.length === 0) {
      invoicesListEl.innerHTML = '<div class="caption" style="color:var(--fg-mute);padding:16px 0;">No invoices yet.</div>';
      return;
    }
    var rows = invoices.map(function (inv) {
      // Enum per wholesale_invoices_status_check: open | paid | overdue |
      // written_off. The raw value used to render verbatim, so a buyer saw
      // the literal string "written_off".
      var statusClass = 'status status-' + inv.status;
      var statusLabel = {
        open: 'Open',
        overdue: 'Overdue',
        paid: 'Paid',
        written_off: 'Written off',
      }[inv.status] || inv.status;
      // SETTLED, not PAID. written_off became reachable when the invoice
      // admin routes shipped, and it was rendering as money still owed.
      // Both are settled so neither amount is struck as outstanding, but they
      // are NOT the same thing and the buyer must be able to tell: the strike
      // is shared, the distinction is carried by the status pill (Paid is
      // green and solid, Written off is muted with a dashed border) and by
      // the Paid column, which stays "—" for a written-off invoice because
      // paid_at is null.
      var settled = inv.status === 'paid' || inv.status === 'written_off';
      var amountCell = settled
        ? '<span style="color:var(--fg-mute);text-decoration:line-through;">' + fmtCad(inv.amount_cad) + '</span>'
        : fmtCad(inv.amount_cad);
      // 4Ae-PDF-b: per-row download link. Click handler delegates
      // from the table (single listener attached after render). The
      // href is informational only (data-attr does the work) — we
      // POST nothing; the GET happens via fetch in onInvoicePdfClick
      // so the buyer-safe error copy can be inlined on failure.
      var pdfCell = '<a href="#" data-wholesale-invoice-pdf="' + escapeHtml(inv.invoice_number) +
        '" class="caption" style="color:var(--drift);font-family:var(--mono);font-size:11px;letter-spacing:0.04em;text-transform:uppercase;">Download</a>';
      return '<tr>' +
        '<td><strong>' + escapeHtml(inv.invoice_number) + '</strong></td>' +
        '<td>' + fmtDate(inv.issued_at) + '</td>' +
        '<td>' + fmtDate(inv.due_at) + '</td>' +
        '<td>' + amountCell + '</td>' +
        '<td><span class="' + statusClass + '">' + escapeHtml(statusLabel) + '</span></td>' +
        '<td>' + (inv.paid_at ? fmtDate(inv.paid_at) : '—') + '</td>' +
        '<td>' + pdfCell + '</td>' +
      '</tr>';
    }).join('');
    invoicesListEl.innerHTML =
      '<table class="wholesale-table">' +
        '<thead><tr>' +
          '<th>Invoice</th><th>Issued</th><th>Due</th><th>Amount</th><th>Status</th><th>Paid</th><th>PDF</th>' +
        '</tr></thead>' +
        '<tbody>' + rows + '</tbody>' +
      '</table>';
    // Wire row-level PDF clicks (delegated).
    invoicesListEl.addEventListener('click', onInvoicePdfClick);
  }

  // ─── Invoice PDF download (4Ae-PDF-b) ───────────────────────

  // Same late-binding reason as wholesale-order.js: this map is a module-level
  // literal evaluated at load, and circadia-chrome.js (which owns the shared
  // string) is deferred with no guaranteed order.
  var SIGN_IN_SENTINEL = '__SIGN_IN_REQUIRED__';

  function errorCopy(map, code, fallback) {
    var msg = map[code] || fallback;
    return msg === SIGN_IN_SENTINEL
      ? (window.Circadia && window.Circadia.SIGN_IN_REQUIRED) || fallback
      : msg;
  }

  var INVOICE_PDF_ERROR_COPY = {
    unauthorized: SIGN_IN_SENTINEL,
    invoice_not_found:       'Invoice not found.',
    not_a_wholesale_account: 'This isn’t a wholesale account.',
    wholesale_access_inactive: 'Your wholesale access isn’t active right now.',
    shopify_unavailable:     'Could not generate the invoice right now, please try again.',
    shopify_fetch_failed:    'Could not generate the invoice right now, please try again.',
    render_failed:           'Could not generate the invoice right now, please try again.',
    storage_failed:          'Could not generate the invoice right now, please try again.',
    order_row_missing:       'Could not generate the invoice (missing order data). Please contact support.',
  };

  function onInvoicePdfClick(ev) {
    var link = ev.target.closest('a[data-wholesale-invoice-pdf]');
    if (!link) return;
    ev.preventDefault();
    var invoiceNumber = link.getAttribute('data-wholesale-invoice-pdf');
    if (!invoiceNumber) return;
    var prevLabel = link.textContent;
    link.textContent = 'Preparing…';
    link.style.pointerEvents = 'none';
    fetch('/apps/dashboard/wholesale/invoices/' + encodeURIComponent(invoiceNumber) + '/pdf', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        // 401 before parsing; not guaranteed to be JSON.
        if (r.status === 401) {
          return { status: 401, body: { code: 'unauthorized' } };
        }
        return r.json().then(function (body) {
          return { status: r.status, body: body };
        });
      })
      .then(function (resp) {
        if (resp.status === 200 && resp.body && resp.body.pdf_url) {
          window.open(resp.body.pdf_url, '_blank', 'noopener');
          link.textContent = prevLabel;
          link.style.pointerEvents = '';
          return;
        }
        var code = (resp.body && resp.body.code) || '';
        var msg = errorCopy(INVOICE_PDF_ERROR_COPY, code, 'Could not download the invoice, please try again.');
        showInvoiceError(link, msg);
        link.textContent = prevLabel;
        link.style.pointerEvents = '';
      })
      .catch(function () {
        showInvoiceError(link, 'Could not download the invoice, please try again.');
        link.textContent = prevLabel;
        link.style.pointerEvents = '';
      });
  }

  function showInvoiceError(linkEl, msg) {
    var row = linkEl.closest('tr');
    if (!row) return;
    var existing = row.querySelector('[data-wholesale-invoice-error]');
    if (existing) existing.parentNode.removeChild(existing);
    var div = document.createElement('div');
    div.setAttribute('data-wholesale-invoice-error', '');
    div.className = 'caption';
    div.style.color = '#c0392b';
    div.style.fontSize = '11px';
    div.style.marginTop = '2px';
    div.textContent = msg;
    linkEl.parentNode.appendChild(div);
  }

  // ─── Fetch + dispatch ───────────────────────────────────────────

  fetch(ME_URL, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then(function (r) {
      // Before r.json() and before the generic !r.ok throw.
      if (r.status === 401) {
        throw new Error('unauthorized');
      }
      if (r.status === 404) {
        // Finding 7. TWO distinguishable states, keyed on the reason CODE rather
        // than on any status value: the raw application_status never reaches the
        // browser. 'wholesale_access_inactive' means a relationship exists but is
        // not currently active; anything else (including an unreadable body)
        // falls back to the never-applied copy, which is the safe direction.
        return r
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            renderNotAWholesaleAccount(body && body.code);
            return null;
          });
      }
      if (!r.ok) {
        throw new Error('HTTP ' + r.status);
      }
      return r.json();
    })
    .then(function (body) {
      if (!body) return; // 404 already handled
      renderHeader(body);
      renderKpis(body);
      renderOrders(body.orders);
      renderInvoices(body.invoices);
    })
    .catch(function (err) {
      if (kpisEl) {
        var dashMsg = err && err.message === 'unauthorized'
          ? (window.Circadia && window.Circadia.SIGN_IN_REQUIRED) || ''
          : 'Could not load dashboard: ' + String(err && err.message || err);
        kpisEl.innerHTML = '<div class="caption" style="color:#c0392b;">' + escapeHtml(dashMsg) + '</div>';
      }
    });
})();
